import { FC, useState } from 'react';
import { IonButton, IonSpinner } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Modal } from '../components/Modal';
import { TextField } from '../components/TextField';
import { EmailField } from '../contact-methods/EmailField';
import { ReferralService } from './ReferralService';
import { AlertPresenter } from '../AlertPresenter';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { ToastPresenter } from '../ToastBinder';
import { DuoGiftIcon } from '../theme/Icons';

const useStyles = createUseStyles({
    content: {
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 20px 20px 20px',
        height: '100%'
    },

    iconWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 16
    },

    iconCircle: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: '#EDF4F7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    description: {
        textAlign: 'center',
        marginBottom: 32,
        color: 'var(--ion-color-medium-shade)',
        fontSize: 14,
        lineHeight: 1.4
    },

    textFieldWrapper: {
        marginBottom: 12
    },

    buttonRow: {
        display: 'flex',
        gap: 12,
        marginTop: 'auto',
        paddingTop: 20
    },

    button: {
        flex: 1
    }
});

interface IReferralInviteModalProps {
    isOpen: boolean;
    closeHandler: () => void;
}

export const ReferralInviteModal: FC<IReferralInviteModalProps> = ({
    isOpen,
    closeHandler
}) => {
    const classes = useStyles();

    const referralService = useInstance(ReferralService);
    const alertPresenter = useInstance(AlertPresenter);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const toastPresenter = useInstance(ToastPresenter);

    const [inviteeEmail, setInviteeEmail] = useState('');
    const [inviteeName, setInviteeName] = useState('');
    const [emailValid, setEmailValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const clearForm = () => {
        setInviteeEmail('');
        setInviteeName('');
    };

    const handleSubmit = async () => {
        if (!emailValid) {
            return;
        }

        setIsLoading(true);

        try {
            const result = await referralService.sendInvite({
                inviteeEmail,
                inviteeName: inviteeName || undefined
            });

            mixpanel.track('Referral Sent', () => ({
                'Invitee Email': inviteeEmail,
                'Invitee Name': inviteeName || '',
                'Is Existing User': result.isExistingUser
            }));

            closeHandler();

            if (result.isExistingUser) {
                mixpanel.track('Referral Blocked (Existing User)', () => ({
                    'Invitee Email': inviteeEmail,
                    'Invitee Name': inviteeName || ''
                }));

                await alertPresenter.showAlertV2({
                    header: 'Already on Innerhive',
                    message: 'This person already has an Innerhive account.',
                    options: [{ id: 'ok', label: 'OK' }]
                });
            } else {
                toastPresenter.toast({ message: 'Invitation sent!', duration: 3000 });
            }
        } catch (error: any) {
            mixpanel.track('Referral Invite Failed', () => ({
                'Error': error?.message || 'Unknown error'
            }));
            alertPresenter.showAndLogError(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            data-id="referral-invite-modal"
            title="Invite a Friend"
            centerTitle
            closeHandler={closeHandler}
            onWillPresent={clearForm}
            desktopMaxHeight={500}
        >
            <div className={classes.content}>
                <div className={classes.iconWrapper}>
                    <div className={classes.iconCircle}>
                        <DuoGiftIcon customSize={36} color="primary" />
                    </div>
                </div>

                <div className={classes.description}>
                    Know someone caring for a loved one? Help them get the support they deserve.
                </div>

                <div className={classes.textFieldWrapper}>
                    <EmailField
                        required
                        method={{ type: 'email', value: inviteeEmail }}
                        methodChanged={(m) => setInviteeEmail(m.value)}
                        label="Friend's Email"
                        data-id="referral-email-field"
                        isValid={setEmailValid}
                    />
                </div>

                <div className={classes.textFieldWrapper}>
                    <TextField
                        name="name"
                        value={inviteeName}
                        label="Friend's Name"
                        data-id="referral-name-field"
                        autocapitalize="words"
                        onInput={(value) => setInviteeName(value)}
                        blurOnEnter
                    />
                </div>

                <div className={classes.buttonRow}>
                    <IonButton
                        className={classes.button}
                        fill="outline"
                        data-id="referral-cancel-button"
                        onClick={closeHandler}
                    >
                        Cancel
                    </IonButton>
                    <IonButton
                        className={classes.button}
                        data-id="referral-submit-button"
                        disabled={!emailValid || isLoading}
                        onClick={handleSubmit}
                    >
                        {isLoading ? <IonSpinner name="dots" style={{height: 20}} /> : 'Send'}
                    </IonButton>
                </div>
            </div>
        </Modal>
    );
};
